/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { ButtonList, CosmosRichText, CosmosText, CosmosTextVariantEnum } from "@components/elements";
import type { CosmosButtonProps } from "@components/elements";
import { css, cx } from "@panda/css";
import { Box } from "@panda/jsx";

import { featureCardBodyStyles, featureCardMediaStyles, featureCardStyles } from "./AccordionTierCard.styles";

import type { AccordionTierFeatureCardFragment } from "@cosmos/hygraph-schema";

const mediaImgStyles = css({
	position: "absolute",
	inset: 0,
	width: "100%",
	height: "100%",
	objectFit: "cover",
	borderRadius: "eds.borderRadius.8",
});

/**
 * Buttons cell wrapper. Below lg the buttons block flows naturally inside the
 * column body. At lg+ it's pinned to 245px (Figma `w-[245px]`) with the buttons
 * `justify-end` inside — so the rightmost button always sits at the right edge
 * of the cell, matching Figma's column-aligned right edge. With `justify-end`,
 * single-button cards (e.g. Cheat Detection) push "Documentation" to the right;
 * two-button cards (e.g. Sanctions) keep "Learn more" at the right edge with
 * "Documentation" just left of it.
 */
const featureCardButtonsStyles = css({
	display: "flex",
	flexShrink: 0,
	// `245px` (not `15.3125rem`) — see AccordionTierCard.styles.ts comments about
	// decimal-rem class generation hazards under Panda + HMR.
	lg: {
		width: "245px",
		justifyContent: "flex-end",
	},
});

export type TierFeatureCardProps = AccordionTierFeatureCardFragment;

/**
 * One feature row inside a tier (e.g. "Cheat Prevention", "Sanctions"). Image asset
 * sits to the left of the title + desc on desktop, full-width above on mobile.
 * Buttons (Documentation / Learn more) pin right on desktop, stack below on mobile.
 *
 * The image asset is uploaded by the author through Hygraph — the component just
 * renders whatever URL the CMS gives it.
 */
export const TierFeatureCard = ({ title, desc, asset, buttons }: TierFeatureCardProps): React.JSX.Element | null => {
	if (!title?.html && !title?.text && !desc) return null;

	return (
		<Box className={featureCardStyles}>
			{asset?.url ? (
				<div className={featureCardMediaStyles}>
					<img className={mediaImgStyles} src={asset.url} alt={asset.altText || ""} />
				</div>
			) : null}

			<Box className={featureCardBodyStyles}>
				<div className="feature-card-text">
					{title?.text || title?.html ? (
						<CosmosText className={cx("feature-card-title")} variant={CosmosTextVariantEnum.headingSM} as="h4">
							{title.text || title.html}
						</CosmosText>
					) : null}
					{desc ? (
						<CosmosRichText
							className="feature-card-desc"
							variant={CosmosTextVariantEnum.paragraphSM}
							as="div"
							html={desc}
						/>
					) : null}
				</div>
				{buttons?.length ? (
					// Wrapper pins the cell to 245px (Figma `w-[245px]`) at lg+ with `justify-end`
					// so the rightmost button always lands at the same x position across cards
					// regardless of single- or multi-button layouts. Below lg the cell flows
					// naturally inside the column body.
					//
					// `as CosmosButtonProps[]` — `buttons` is a `CosmosModButtonFragment[]` (a
					// union of `CosmosElementButton | CosmosElementIconButton`). `ButtonList`
					// declares its prop as `CosmosButtonFragment[]`, but at runtime it spreads
					// each entry into `CosmosButton`, which already accepts `icon` and
					// `iconPosition` from icon-buttons via its props interface — so the cast is
					// safe; only the static type is too narrow.
					<div className={featureCardButtonsStyles}>
						<ButtonList buttons={buttons as CosmosButtonProps[]} size="md" />
					</div>
				) : null}
			</Box>
		</Box>
	);
};

TierFeatureCard.displayName = "AccordionTierCard.FeatureCard";
